import { Button } from '@react-spectrum/s2';
import { useCallback, useMemo, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { CommentWatcher } from '@/components/artifact/CommentWatcher';
import { resolveCommentWatcherMode } from '@/components/artifact/commentWatcherUtils';
import { GenericReview } from '@/components/artifact/GenericReview';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useJobActivation } from '@/components/artifact/useJobActivation';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { trackEvent } from '@/utils/fullstory';

import type {
	Artifact,
	IGCommentWatcherClassContent,
} from '../../../../../bridge/types/artifact.ts';
import { hasFunnelSibling } from '../../../../../bridge/utils/setupCardItems.ts';

interface CommentWatcherArtifactPanelProps {
	artifact: Artifact;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

const COMMENT_WATCHER_PROMPT_FIELDS = [
	'condition',
	'reply_prompt',
	'dm_prompt',
] as const;

function isIGCommentWatcherContent(
	content: Record<string, unknown>,
): content is IGCommentWatcherClassContent {
	return (
		typeof content.name === 'string' &&
		typeof content.condition === 'string' &&
		typeof content.type === 'string'
	);
}

export const CommentWatcherArtifactPanel = ({
	artifact,
	onBack,
	onClose,
	style,
}: CommentWatcherArtifactPanelProps) => {
	const bridgeApi = useBridgeApi();
	const [actionTaken, setActionTaken] = useState<'paused' | 'resumed' | null>(
		null,
	);
	useEnsureSetupSnapshot(artifact.process_id);
	const goToNextItem = useGoToNextStep();
	const { handleActivate, activatedView } = useJobActivation({
		artifact,
		title: 'Comment watcher activated',
		summary:
			"I'll watch new Instagram comments and reply based on the rules you set.",
		onClose,
	});

	// Funnel sibling vs standalone: when this watcher is one of several items in
	// a process (e.g. a carousel + watcher funnel), it's a setup step — confirming
	// it marks the item complete and advances, NOT activating the whole process.
	// When it's the only child ("watch all my comments"), keep the direct Activate.
	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	// Default to standalone (existing Activate behavior) until the snapshot loads,
	// so a standalone watcher never briefly shows the funnel "Done" button.
	const isStandalone = !hasFunnelSibling(snapshot);
	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Comment watcher confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (artifact.process_id) goToNextItem(artifact.process_id, artifact.id);
		},
	});

	const handleSubmitStep = useCallback(() => {
		if (!artifact.process_id) return;
		trackEvent('Comment Watcher Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		markSubmitted();
	}, [bridgeApi, artifact.id, artifact.process_id, markSubmitted]);

	const handleContentChange = useCallback(
		(updates: Record<string, unknown>) => {
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: artifact.id,
				edits: updates,
			});
			if (artifact.status !== 'active' && artifact.status !== 'paused') {
				const changedField = COMMENT_WATCHER_PROMPT_FIELDS.find(
					(f) => f in updates,
				);
				if (changedField) {
					bridgeApi.sendAction({
						type: 'do_generate_comment_watcher_examples',
						artifact_id: artifact.id,
						field: changedField,
						prompt: String(updates[changedField]),
					});
				}
			}
		},
		[bridgeApi, artifact.id, artifact.status],
	);

	const handlePause = useCallback(() => {
		trackEvent('Process Paused', {
			artifact_id: artifact.id,
			process_id: artifact.process_id,
		});
		setActionTaken('paused');
		// Guarded: do_pause_process requires a process id, and its post-effect calls
		// the API with it — dispatching without one produces a malformed request.
		if (artifact.process_id) {
			bridgeApi.sendAction({
				type: 'do_pause_process',
				process_id: artifact.process_id,
			});
		}
	}, [bridgeApi, artifact.id, artifact.process_id]);

	const handleResume = useCallback(() => {
		trackEvent('Process Resumed', {
			artifact_id: artifact.id,
			process_id: artifact.process_id,
		});
		setActionTaken('resumed');
		if (artifact.process_id) {
			bridgeApi.sendAction({
				type: 'do_resume_process',
				process_id: artifact.process_id,
			});
		}
	}, [bridgeApi, artifact.id, artifact.process_id]);

	const typedContent = useMemo(() => {
		return isIGCommentWatcherContent(artifact.content)
			? artifact.content
			: null;
	}, [artifact.content]);

	const isReadOnly =
		artifact.status === 'active' || artifact.status === 'paused';
	const title = TYPE_TITLES[artifact.type] ?? 'Review Comment Watcher';
	const isActionable = isArtifactActionable(artifact) && !actionTaken;
	// Standalone: directly activate the whole (single-item) process.
	const showActivateButton = isActionable && isStandalone;
	// Funnel sibling: confirm this step; activation happens later for the funnel.
	// Labeled "Approve" to match the other setup-card review steps (e.g. posts).
	const showSubmitButton = isActionable && !isStandalone;
	const showPauseButton = artifact.status === 'active' && !actionTaken;
	const showResumeButton = artifact.status === 'paused' && !actionTaken;
	const latchMessage =
		actionTaken === 'paused'
			? 'Paused successfully'
			: actionTaken === 'resumed'
				? 'Resumed successfully'
				: null;
	const hasActionButton =
		showActivateButton ||
		showSubmitButton ||
		showPauseButton ||
		showResumeButton;
	const actionRenderer =
		hasActionButton || latchMessage
			? () => (
					<>
						{latchMessage && (
							<p className="mr-auto text-sm font-medium text-gray-700">
								{latchMessage}
							</p>
						)}
						{showActivateButton && (
							<Button type="button" variant="accent" onPress={handleActivate}>
								Activate
							</Button>
						)}
						{showSubmitButton && (
							<Button type="button" variant="accent" onPress={handleSubmitStep}>
								Approve
							</Button>
						)}
						{showPauseButton && (
							<Button type="button" variant="accent" onPress={handlePause}>
								Pause
							</Button>
						)}
						{showResumeButton && (
							<Button type="button" variant="accent" onPress={handleResume}>
								Resume
							</Button>
						)}
					</>
				)
			: undefined;

	const mode = resolveCommentWatcherMode(typedContent?.type);

	const overlayView = activatedView ?? submittedView;

	return (
		<ChatPanelLayout
			title={title}
			description={
				overlayView
					? undefined
					: artifact.user_summary || artifact.description || undefined
			}
			actionRenderer={overlayView ? undefined : actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{overlayView ??
				(typedContent ? (
					<CommentWatcher
						content={typedContent}
						mode={mode}
						isReadOnly={isReadOnly}
						modeEditable={isActionable}
						onContentChange={handleContentChange}
					/>
				) : (
					<GenericReview
						content={artifact.content}
						artifactType={artifact.type}
					/>
				))}
		</ChatPanelLayout>
	);
};
